import { CodeIcon } from "../../components/ui/Icons";
import { Note } from "./shared";

export function CoreConceptsDocs() {
  return (
    <>
      {/* What is a Plugin? */}
      <section id="what-is-plugin" className="mb-16 scroll-mt-28">
        <h2 className="flex items-center gap-3 font-display text-2xl font-bold text-primary">
          <CodeIcon size={24} className="text-amber" />
          What is a Plugin?
        </h2>
        <p className="mt-4 text-lg text-secondary">
          A <strong>plugin</strong> is a package that extends Claude Code's capabilities. Think of
          it as an add-on that gives Claude new abilities, knowledge, or behaviors.
        </p>
        <div className="mt-6 rounded-xl border border-elevated bg-surface/30 p-6">
          <p className="text-secondary">
            <strong>Real-world analogy:</strong> Plugins are like apps on your phone. Your phone
            comes with basic functionality, but apps let you add new features—a camera app for
            photos, a music app for streaming, a notes app for writing. Similarly, Claude Code comes
            with core features, but plugins let you add specialized capabilities.
          </p>
        </div>
        <p className="mt-6 text-secondary">
          A single plugin can contain any combination of these components:
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-6 text-secondary">
          <li>
            <strong>Commands</strong> — Shortcuts you invoke with{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">/</code>
          </li>
          <li>
            <strong>Agents</strong> — Specialized assistants for specific tasks
          </li>
          <li>
            <strong>Skills</strong> — Knowledge and capabilities Claude can use automatically
          </li>
          <li>
            <strong>Hooks</strong> — Automated actions triggered by events
          </li>
          <li>
            <strong>MCP Servers</strong> — Connections to external tools and services
          </li>
          <li>
            <strong>LSP Servers</strong> — Code intelligence for programming languages
          </li>
        </ul>
        <div className="mt-6">
          <Note>
            <strong>Key insight:</strong> Not every plugin needs all components. A simple plugin
            might just add one command, while a comprehensive plugin might include agents, skills,
            hooks, and MCP servers working together.
          </Note>
        </div>
      </section>

      {/* What is a Command? */}
      <section id="what-is-command" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">What is a Command?</h2>
        <p className="mt-4 text-lg text-secondary">
          A <strong>command</strong> is a shortcut you type to trigger a specific action. Commands
          start with{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">/</code>{" "}
          followed by a name.
        </p>
        <div className="mt-6 rounded-xl border border-elevated bg-surface/30 p-6">
          <p className="text-secondary">
            <strong>Real-world analogy:</strong> Commands are like keyboard shortcuts. Instead of
            clicking through menus, you press a key combination. Instead of explaining what you
            want, you type{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">/deploy</code> and
            Claude knows exactly what to do.
          </p>
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">Example Commands</h3>
        <div className="mt-4 space-y-3">
          <div className="flex items-start gap-3">
            <code className="shrink-0 rounded bg-surface px-2 py-1 font-mono text-sm text-amber">
              /commit
            </code>
            <span className="text-secondary">
              Create a git commit with an auto-generated message
            </span>
          </div>
          <div className="flex items-start gap-3">
            <code className="shrink-0 rounded bg-surface px-2 py-1 font-mono text-sm text-amber">
              /review
            </code>
            <span className="text-secondary">Review code changes and suggest improvements</span>
          </div>
          <div className="flex items-start gap-3">
            <code className="shrink-0 rounded bg-surface px-2 py-1 font-mono text-sm text-amber">
              /deploy staging
            </code>
            <span className="text-secondary">Deploy the current branch to staging environment</span>
          </div>
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">When to Use Commands</h3>
        <ul className="mt-4 list-disc space-y-2 pl-6 text-secondary">
          <li>
            When you have a <strong>repetitive workflow</strong> you want to standardize
          </li>
          <li>
            When you want to <strong>encode team conventions</strong> (commit message formats,
            deployment steps)
          </li>
          <li>
            When you need a <strong>quick shortcut</strong> for complex multi-step tasks
          </li>
        </ul>
        <h3 className="mt-8 text-lg font-semibold text-secondary">
          Commands vs. Just Asking Claude
        </h3>
        <p className="mt-2 text-secondary">
          You could ask Claude "please commit my changes with a descriptive message" every time. But{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">/commit</code> is
          faster, more consistent, and ensures the same process every time—including any custom
          rules your team has defined.
        </p>
      </section>

      {/* What is an Agent? */}
      <section id="what-is-agent" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">What is an Agent?</h2>
        <p className="mt-4 text-lg text-secondary">
          An <strong>agent</strong> (or subagent) is a specialized assistant that focuses on a
          specific type of task. When Claude encounters a task that matches an agent's expertise, it
          can delegate the work.
        </p>
        <div className="mt-6 rounded-xl border border-elevated bg-surface/30 p-6">
          <p className="text-secondary">
            <strong>Real-world analogy:</strong> Agents are like specialist consultants. A general
            contractor manages your home renovation, but they bring in an electrician for wiring, a
            plumber for pipes, and a painter for finishing. Similarly, Claude is the general
            assistant, but agents are specialists it can call upon.
          </p>
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">Example Agents</h3>
        <div className="mt-4 space-y-4">
          <div className="rounded-lg border border-surface bg-base p-4">
            <p className="font-semibold text-primary">Security Reviewer</p>
            <p className="mt-1 text-sm text-muted">
              Analyzes code for security vulnerabilities, checks for common attack vectors, and
              suggests hardening measures.
            </p>
          </div>
          <div className="rounded-lg border border-surface bg-base p-4">
            <p className="font-semibold text-primary">Test Writer</p>
            <p className="mt-1 text-sm text-muted">
              Generates comprehensive test suites, identifies edge cases, and ensures proper test
              coverage.
            </p>
          </div>
          <div className="rounded-lg border border-surface bg-base p-4">
            <p className="font-semibold text-primary">Database Architect</p>
            <p className="mt-1 text-sm text-muted">
              Designs efficient database schemas, optimizes queries, and suggests indexing
              strategies.
            </p>
          </div>
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">How Agents Get Invoked</h3>
        <ul className="mt-4 list-disc space-y-2 pl-6 text-secondary">
          <li>
            <strong>Automatically:</strong> Claude recognizes when a task matches an agent's
            expertise and delegates
          </li>
          <li>
            <strong>Manually:</strong> You can explicitly request an agent through the{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">/agents</code>{" "}
            interface
          </li>
        </ul>
        <h3 className="mt-8 text-lg font-semibold text-secondary">Agents vs. Commands</h3>
        <p className="mt-2 text-secondary">
          <strong>Commands</strong> are triggered by you explicitly. <strong>Agents</strong> can be
          invoked automatically by Claude when it recognizes the need. Commands are like pressing a
          button; agents are like having a team member who jumps in when they see their expertise is
          needed.
        </p>
      </section>

      {/* What is a Skill? */}
      <section id="what-is-skill" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">What is a Skill?</h2>
        <p className="mt-4 text-lg text-secondary">
          A <strong>skill</strong> is specialized knowledge or capability that Claude can use when
          relevant. Unlike commands (which you invoke) or agents (which handle tasks), skills are
          background capabilities that enhance Claude's responses.
        </p>
        <div className="mt-6 rounded-xl border border-elevated bg-surface/30 p-6">
          <p className="text-secondary">
            <strong>Real-world analogy:</strong> Skills are like training or expertise someone has
            internalized. A chef doesn't consciously think "now I'll use my knife skills"—they just
            use them naturally when cooking. Similarly, Claude doesn't announce "using the PDF
            skill"—it just knows how to handle PDFs when you work with them.
          </p>
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">Example Skills</h3>
        <div className="mt-4 space-y-4">
          <div className="rounded-lg border border-surface bg-base p-4">
            <p className="font-semibold text-primary">Company Style Guide</p>
            <p className="mt-1 text-sm text-muted">
              Knows your organization's coding standards, naming conventions, and architectural
              patterns. Claude applies these automatically when writing code.
            </p>
          </div>
          <div className="rounded-lg border border-surface bg-base p-4">
            <p className="font-semibold text-primary">API Documentation</p>
            <p className="mt-1 text-sm text-muted">
              Contains detailed knowledge of an API's endpoints, authentication, and best practices.
              Claude references this when you work with that API.
            </p>
          </div>
          <div className="rounded-lg border border-surface bg-base p-4">
            <p className="font-semibold text-primary">Framework Expertise</p>
            <p className="mt-1 text-sm text-muted">
              Deep knowledge of a specific framework's patterns, gotchas, and recommended
              approaches. Claude uses this to write better framework-specific code.
            </p>
          </div>
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">Skills vs. Agents</h3>
        <p className="mt-2 text-secondary">
          <strong>Agents</strong> take over and handle complete tasks. <strong>Skills</strong>{" "}
          enhance Claude's existing capabilities without taking control. An agent is like delegating
          to a specialist; a skill is like Claude having studied and internalized expertise.
        </p>
        <h3 className="mt-8 text-lg font-semibold text-secondary">When to Create Skills</h3>
        <ul className="mt-4 list-disc space-y-2 pl-6 text-secondary">
          <li>
            When you have <strong>domain knowledge</strong> Claude should always apply
          </li>
          <li>
            When you want Claude to <strong>follow specific patterns</strong> without being asked
          </li>
          <li>
            When you have <strong>reference documentation</strong> Claude should know about
          </li>
        </ul>
      </section>

      {/* What is a Hook? */}
      <section id="what-is-hook" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">What is a Hook?</h2>
        <p className="mt-4 text-lg text-secondary">
          A <strong>hook</strong> is an automated action that triggers in response to specific
          events. Hooks run automatically without you needing to invoke them.
        </p>
        <div className="mt-6 rounded-xl border border-elevated bg-surface/30 p-6">
          <p className="text-secondary">
            <strong>Real-world analogy:</strong> Hooks are like automatic home features. When you
            open the front door, the lights turn on. When the sun sets, the blinds close. You don't
            press a button—it just happens. Similarly, hooks trigger automatically when certain
            events occur in Claude Code.
          </p>
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">Example Hooks</h3>
        <div className="mt-4 space-y-4">
          <div className="rounded-lg border border-surface bg-base p-4">
            <div className="flex items-center gap-2">
              <span className="rounded bg-amber/20 px-2 py-0.5 font-mono text-xs text-amber">
                PostToolUse
              </span>
              <span className="text-sm text-muted">→ After Claude writes a file</span>
            </div>
            <p className="mt-2 text-secondary">
              Automatically run a code formatter (Prettier, Black) on any file Claude creates or
              modifies.
            </p>
          </div>
          <div className="rounded-lg border border-surface bg-base p-4">
            <div className="flex items-center gap-2">
              <span className="rounded bg-amber/20 px-2 py-0.5 font-mono text-xs text-amber">
                PreToolUse
              </span>
              <span className="text-sm text-muted">→ Before Claude runs a command</span>
            </div>
            <p className="mt-2 text-secondary">
              Block dangerous commands (like{" "}
              <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">rm -rf</code>) or
              require confirmation for sensitive operations.
            </p>
          </div>
          <div className="rounded-lg border border-surface bg-base p-4">
            <div className="flex items-center gap-2">
              <span className="rounded bg-amber/20 px-2 py-0.5 font-mono text-xs text-amber">
                SessionStart
              </span>
              <span className="text-sm text-muted">→ When a session begins</span>
            </div>
            <p className="mt-2 text-secondary">
              Load project context, check for updates, or set up the environment automatically.
            </p>
          </div>
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">Common Hook Events</h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="text-sm">
            <code className="text-amber">PreToolUse</code> — Before Claude uses a tool
          </div>
          <div className="text-sm">
            <code className="text-amber">PostToolUse</code> — After Claude uses a tool
          </div>
          <div className="text-sm">
            <code className="text-amber">SessionStart</code> — When a session begins
          </div>
          <div className="text-sm">
            <code className="text-amber">SessionEnd</code> — When a session ends
          </div>
          <div className="text-sm">
            <code className="text-amber">UserPromptSubmit</code> — When you send a message
          </div>
          <div className="text-sm">
            <code className="text-amber">Stop</code> — When Claude stops responding
          </div>
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">When to Use Hooks</h3>
        <ul className="mt-4 list-disc space-y-2 pl-6 text-secondary">
          <li>
            <strong>Enforcement:</strong> Ensure code is always formatted, linted, or validated
          </li>
          <li>
            <strong>Safety:</strong> Block or warn about dangerous operations
          </li>
          <li>
            <strong>Automation:</strong> Trigger workflows automatically (run tests after changes,
            notify team of deployments)
          </li>
        </ul>
      </section>

      {/* What is MCP? */}
      <section id="what-is-mcp" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">What is MCP?</h2>
        <p className="mt-4 text-lg text-secondary">
          <strong>MCP (Model Context Protocol)</strong> is a way to connect Claude Code to external
          tools and services. MCP servers give Claude new capabilities by connecting it to
          databases, APIs, and other systems.
        </p>
        <div className="mt-6 rounded-xl border border-elevated bg-surface/30 p-6">
          <p className="text-secondary">
            <strong>Real-world analogy:</strong> MCP is like giving Claude a set of power tools. On
            its own, Claude can do a lot. But connect it to your database, your cloud provider, or
            your project management system, and suddenly it can query data, spin up servers, or
            create tickets—all through natural conversation.
          </p>
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">Example MCP Servers</h3>
        <div className="mt-4 space-y-4">
          <div className="rounded-lg border border-surface bg-base p-4">
            <p className="font-semibold text-primary">Database Server</p>
            <p className="mt-1 text-sm text-muted">
              Query your database directly. Ask Claude "show me users who signed up last week" and
              it runs the query.
            </p>
          </div>
          <div className="rounded-lg border border-surface bg-base p-4">
            <p className="font-semibold text-primary">GitHub Server</p>
            <p className="mt-1 text-sm text-muted">
              Create issues, review PRs, manage branches—all through natural language.
            </p>
          </div>
          <div className="rounded-lg border border-surface bg-base p-4">
            <p className="font-semibold text-primary">Cloud Provider Server</p>
            <p className="mt-1 text-sm text-muted">
              Check server status, view logs, or deploy changes without leaving the conversation.
            </p>
          </div>
        </div>
        <h3 className="mt-8 text-lg font-semibold text-secondary">MCP vs. Other Components</h3>
        <p className="mt-2 text-secondary">
          While commands, agents, and skills enhance what Claude can think and do,{" "}
          <strong>MCP connects Claude to external systems</strong>. It's the bridge between Claude's
          intelligence and your actual infrastructure.
        </p>
      </section>

      {/* How They Work Together */}
      <section id="how-they-work-together" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">How They Work Together</h2>
        <p className="mt-4 text-lg text-secondary">
          The power of plugins comes from combining these components. Here's how they might work
          together in a real scenario:
        </p>

        <div className="mt-8 rounded-xl border border-elevated bg-surface/30 p-6">
          <h3 className="font-semibold text-primary">Scenario: Deploying a Feature</h3>
          <p className="mt-2 text-muted">
            You type{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
              /deploy production
            </code>
          </p>

          <div className="mt-6 space-y-4">
            <div className="flex gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
                1
              </div>
              <div>
                <p className="font-medium text-secondary">Command triggers</p>
                <p className="text-sm text-muted">
                  The <code>/deploy</code> command kicks off the deployment workflow
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
                2
              </div>
              <div>
                <p className="font-medium text-secondary">Skill provides context</p>
                <p className="text-sm text-muted">
                  Claude uses the "deployment procedures" skill to know your team's specific steps
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
                3
              </div>
              <div>
                <p className="font-medium text-secondary">Agent handles security</p>
                <p className="text-sm text-muted">
                  The security agent scans for vulnerabilities before deployment
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
                4
              </div>
              <div>
                <p className="font-medium text-secondary">MCP connects to cloud</p>
                <p className="text-sm text-muted">
                  Claude uses the AWS MCP server to actually deploy to production
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/20 font-mono text-sm text-amber">
                5
              </div>
              <div>
                <p className="font-medium text-secondary">Hook notifies team</p>
                <p className="text-sm text-muted">
                  A PostToolUse hook sends a Slack message announcing the deployment
                </p>
              </div>
            </div>
          </div>
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Quick Reference</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Component</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Triggered By</th>
                <th className="py-3 text-left font-mono text-muted">Best For</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-medium text-amber">Command</td>
                <td className="py-3 pr-4">You (explicitly)</td>
                <td className="py-3">Repeatable workflows, shortcuts</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-medium text-amber">Agent</td>
                <td className="py-3 pr-4">Claude (when needed) or you</td>
                <td className="py-3">Complex specialized tasks</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-medium text-amber">Skill</td>
                <td className="py-3 pr-4">Claude (automatically)</td>
                <td className="py-3">Background knowledge, conventions</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-medium text-amber">Hook</td>
                <td className="py-3 pr-4">Events (automatically)</td>
                <td className="py-3">Automation, enforcement, safety</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-medium text-amber">MCP Server</td>
                <td className="py-3 pr-4">Claude (when connecting to systems)</td>
                <td className="py-3">External integrations</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
